// Fault reports ("Fejlmelding", SCRUM-729): the HUD-side client for games-cloud's
// /fault-reports endpoints. games-cloud owns Jira, the media bucket and the Teams card; the HUD
// only ever talks to games-cloud.
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { GAMES_CLOUD_BASE } from "../../utils/config";
import { formatDate } from "../../utils/format";

const base = `${GAMES_CLOUD_BASE}fault-reports`;

export const CATEGORY_LABELS = { prop: "Prop", teknik: "Teknik", lokale: "Lokale", andet: "Andet" };
export const SEVERITY_LABELS = { lav: "Lav", normal: "Normal", hoej: "Høj", kritisk: "Kritisk" };

// Jira status categories → what the GM sees. Status names themselves come from the Jira project
// and are shown as-is; the category drives the colour so renamed statuses still look right.
export const STATUS_CATEGORY = {
    new: { label: "Åben", cls: "fr-status-open" },
    indeterminate: { label: "I gang", cls: "fr-status-progress" },
    done: { label: "Løst", cls: "fr-status-done" },
};

export async function fetchRooms() {
    const r = await fetchWithAuth(`${base}/rooms`);
    return r.json(); // { rooms, categories, severities }
}

// days limits resolved issues to the last N days when all is set (0 = everything, capped by the
// server at its max, in which case `truncated` is true).
export async function fetchReports({ location, room, all, days } = {}) {
    const q = new URLSearchParams();
    if (location) q.set("location", location);
    if (room) q.set("room", room);
    if (all) {
        q.set("all", "1");
        q.set("days", String(days ?? 0));
    }
    const qs = q.toString();
    const r = await fetchWithAuth(`${base}${qs ? `?${qs}` : ""}`);
    const data = await r.json();
    return { issues: data.issues || [], truncated: !!data.truncated, max: data.max || 0 };
}

export async function fetchReport(key) {
    const r = await fetchWithAuth(`${base}/${encodeURIComponent(key)}`);
    return r.json(); // { issue, photos }
}

export async function createReport(body) {
    const r = await fetchWithAuth(base, { method: "POST", body: JSON.stringify(body) });
    return r.json(); // { key, url, upload_token, expires_at }
}

export async function newUploadToken(key) {
    const r = await fetchWithAuth(`${base}/${encodeURIComponent(key)}/upload-token`, { method: "POST" });
    return r.json(); // { key, upload_token, expires_at }
}

// Photo URLs from games-cloud carry their own per-photo signature (?sig=), so an <img> or a link
// in Jira/Teams opens them without a HUD login. Nothing to add on the HUD side.

// The link the QR code carries: the static phone page on the HUD's own origin, told which issue,
// which games-cloud to talk to and the scoped upload token. The origin is whatever the HUD was
// opened on, so a LAN test (http://<laptop-ip>:3000) just works without configuration.
// defaultPhoneAPI is what the phone page assumes when the link carries no `api`: games-cloud on
// the sibling host of the HUD (prod.gm-hud.… → prod.games-cloud.…). Keeping it out of the link
// keeps the QR code small; it is only included when the HUD talks to something else (local dev).
function defaultPhoneAPI() {
    return `${window.location.protocol}//${window.location.host.replace("gm-hud", "games-cloud")}/api/v1/games-cloud/`;
}

export function uploadPageURL(key, uploadToken, title = "") {
    // Every character here is QR modules, so: short token (see games-cloud), api only when it is
    // not the default, title capped. GAMES_CLOUD_BASE is already LAN-aware (utils/config.js).
    const q = new URLSearchParams({ key, token: uploadToken });
    if (GAMES_CLOUD_BASE !== defaultPhoneAPI()) q.set("api", GAMES_CLOUD_BASE);
    if (title) q.set("t", title.slice(0, 40));
    return `${window.location.origin}/upload.html?${q.toString()}`;
}

// "8. okt. 08:06" — the date through the HUD's shared formatter, the time with a colon. The Danish
// locale writes times as 08.06, which next to a numeric date reads as two dates.
export function formatWhen(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const hhmm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    return `${formatDate(d)} ${hhmm}`;
}

